import { getCurrentApplicationId } from "~/lib/applicationId";

export const STF_BASE_URL = import.meta.env.DEV
  ? "/stf"
  : import.meta.env.VITE_STF_URL;

/** img_kind 定数（iOS URLGenerator 準拠） */
const IMG_KIND = {
  AVATAR: "1",
  PUBLIC_IMAGE: "2",
  STICKER: "3",
  FLEA_MARKET: "6",
  AGORA_SCREENSHOT: "8",
} as const;

/**
 * STF サーバーから画像を取得する URL を生成する（token 不要版）。
 * Next.js 版と同じ load_img_for_utage_web API を使用し、
 * URL に token を含めないことでブラウザキャッシュを有効にする。
 */
const buildImageUrl = (imgId: string, imgKind: string): string => {
  return `${STF_BASE_URL}/api=load_img_for_utage_web&img_id=${encodeURIComponent(imgId)}&img_kind=${imgKind}`;
};

/** アバター / サムネイル画像の URL を生成 */
export const getAvatarUrl = (imgId: string | null): string | null => {
  if (!imgId) return null;
  return buildImageUrl(imgId, IMG_KIND.AVATAR);
};

/** 公開画像（プロフィール写真一覧）の URL を生成 */
export const getPublicImageUrl = (imgId: string): string => {
  return buildImageUrl(imgId, IMG_KIND.PUBLIC_IMAGE);
};

/** ギャラリーの購入済み画像・動画サムネイル URL */
export const getGalleryImageUrl = (fileId: string): string => {
  return buildImageUrl(fileId, IMG_KIND.AVATAR);
};

/** ギャラリーの購入済み動画本体 URL（再生用） */
export const getGalleryVideoUrl = (fileId: string): string => {
  return `${STF_BASE_URL}/api=load_file_for_utage_web&file_id=${encodeURIComponent(fileId)}`;
};

/** フリマ商品画像の URL を生成 */
export const getFleaMarketImageUrl = (imgId: string): string => {
  return buildImageUrl(imgId, IMG_KIND.FLEA_MARKET);
};

/** スタンプ画像の URL を生成（imgId = スタンプコード。STF が S3 から配信） */
export const getStickerImageUrl = (imgId: string): string => {
  return buildImageUrl(imgId, IMG_KIND.STICKER);
};

/** ライブ配信サムネイル（Agora スクリーンショット）の URL を生成 */
export const getLiveThumbnailUrl = (imgId: string): string => {
  return buildImageUrl(imgId, IMG_KIND.AGORA_SCREENSHOT);
};

/**
 * デモ通話（オンボーディング無料通話）のサムネイル URL を生成する（token 不要）。
 * `demo_user_id` は STF 側で許可リスト検証される（Jambo-Server PR #34964）。
 */
export const getDemoCallThumbnailUrl = (demoUserId: string): string => {
  return `${STF_BASE_URL}/api=load_demo_call_thumbnail_for_web&application_id=${encodeURIComponent(getCurrentApplicationId())}&demo_user_id=${encodeURIComponent(demoUserId)}`;
};

/**
 * デモ通話（オンボーディング無料通話）の動画本体 URL を生成する（`<video src>` 直参照用、token 不要）。
 * `demo_user_id` は STF 側で許可リスト検証される（Jambo-Server PR #34964）。
 */
export const getDemoCallMovieUrl = (demoUserId: string): string => {
  return `${STF_BASE_URL}/api=load_demo_call_movie_for_web&application_id=${encodeURIComponent(getCurrentApplicationId())}&demo_user_id=${encodeURIComponent(demoUserId)}`;
};

/** ライブ配信録画動画の URL を生成（token 必須）。recording_id か buzz_file_id のどちらかを渡す */
export const getLiveRecordingUrl = ({
  recordingId,
  buzzFileId,
  token,
}: {
  recordingId: string | null;
  buzzFileId: string | null;
  token: string;
}): string => {
  if (recordingId) {
    return `${STF_BASE_URL}/api=load_live_recording&token=${encodeURIComponent(token)}&file_id=${encodeURIComponent(recordingId)}`;
  }
  return `${STF_BASE_URL}/api=load_live_recording&token=${encodeURIComponent(token)}&buzz_file_id=${encodeURIComponent(buzzFileId ?? "")}`;
};
